package com.eladfinish.bible.contest.ui.components.toolbar.ui

import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material.icons.filled.MoreVert
import androidx.compose.material3.Checkbox
import androidx.compose.material3.CheckboxDefaults
import androidx.compose.material3.DropdownMenuItem
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.RadioButton
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import com.eladfinish.bible.contest.SharedRes
import com.eladfinish.bible.contest.common.gender.HebrewGender
import com.eladfinish.bible.contest.common.gender.personalizeGender
import com.eladfinish.bible.contest.toolbar_menu_more_options
import com.eladfinish.bible.contest.ui.components.toolbar.ToolbarTooltipIfNeeded
import com.eladfinish.bible.contest.ui.components.toolbar.configuration.model.SubMenuContent
import com.eladfinish.bible.contest.ui.components.toolbar.configuration.model.ToolbarActionConfig
import com.eladfinish.bible.contest.ui.icons.AppIcons
import dev.icerock.moko.resources.compose.localized
import dev.icerock.moko.resources.compose.stringResource

@Composable
fun <TId : Enum<TId>> ToolbarOverflowMenu(
    overflowActions: List<ToolbarActionConfig<TId>>,
    hebrewGender: HebrewGender,
    modifier: Modifier = Modifier,
    initialExpanded: Boolean = false,
    initialActiveSubMenuId: TId? = null,
) {
    if (overflowActions.isEmpty()) return

    var expanded by rememberSaveable { mutableStateOf(initialExpanded) }
    var activeSubMenuId by remember { mutableStateOf(initialActiveSubMenuId) }

    OverflowButton(onOpen = { expanded = true })

    ThemedOverflowMenu(
        expanded = expanded,
        onDismissRequest = {
            expanded = false
            activeSubMenuId = null
        },
        modifier = modifier,
    ) {
        val activeSubMenu = activeSubMenuId?.let { id ->
            overflowActions.firstOrNull { it.id == id } as? ToolbarActionConfig.SubMenu<TId>
        }

        if (activeSubMenu == null) {
            OverflowRootContent(
                overflowActions = overflowActions,
                hebrewGender = hebrewGender,
                onOpenSubMenu = { activeSubMenuId = it },
                onDismissAll = {
                    expanded = false
                    activeSubMenuId = null
                }
            )
            return@ThemedOverflowMenu
        }

        OverflowSubMenuContent(
            activeSubMenu = activeSubMenu,
            hebrewGender = hebrewGender,
            onBack = { activeSubMenuId = null },
            onDismissAll = {
                expanded = false
                activeSubMenuId = null
            }
        )
    }
}

@Composable
private fun OverflowButton(
    onOpen: () -> Unit,
) {
    val tooltipText = stringResource(SharedRes.strings.toolbar_menu_more_options)

    ToolbarTooltipIfNeeded(
        tooltipText = tooltipText,
    ) {
        IconButton(
            onClick = onOpen,
        ) {
            Icon(
                imageVector = Icons.Default.MoreVert,
                contentDescription = tooltipText,
                tint = Color.White,
            )
        }
    }
}

@Composable
private fun <TId : Enum<TId>> OverflowRootContent(
    overflowActions: List<ToolbarActionConfig<TId>>,
    hebrewGender: HebrewGender,
    onOpenSubMenu: (TId) -> Unit,
    onDismissAll: () -> Unit,
) {
    overflowActions.forEach { action ->
        when (action) {
            is ToolbarActionConfig.SubMenu -> OverflowSubMenuRow(
                action = action,
                hebrewGender = hebrewGender,
                onOpenSubMenu = onOpenSubMenu,
            )

            is ToolbarActionConfig.CheckboxActionRes<*> -> OverflowCheckboxRow(
                action = action,
                hebrewGender = hebrewGender,
            )

            is ToolbarActionConfig.IconAction<*>,
            is ToolbarActionConfig.TextAction<*>,
            is ToolbarActionConfig.TextActionRes<*>,
                -> OverflowSimpleActionRow(
                action = action,
                hebrewGender = hebrewGender,
                onDismissAll = onDismissAll,
            )
        }
    }
}

@Composable
private fun <TId : Enum<TId>> OverflowSubMenuContent(
    activeSubMenu: ToolbarActionConfig.SubMenu<TId>,
    hebrewGender: HebrewGender,
    onBack: () -> Unit,
    onDismissAll: () -> Unit,
) {
    SubMenuHeader(
        title = activeSubMenu.stringDesc.localized().personalizeGender(hebrewGender),
        onBack = onBack,
    )

    when (val content = activeSubMenu.content) {
        is SubMenuContent.RadioSubMenu<*, *> -> {
            @Suppress("UNCHECKED_CAST")
            RadioSubMenuContent(
                content = content as SubMenuContent.RadioSubMenu<TId, out Enum<*>>,
                hebrewGender = hebrewGender,
                onDismissAll = onDismissAll,
            )
        }
    }
}

@Composable
private fun <TId : Enum<TId>> OverflowSubMenuRow(
    action: ToolbarActionConfig.SubMenu<TId>,
    hebrewGender: HebrewGender,
    onOpenSubMenu: (TId) -> Unit,
) {
    DropdownMenuItem(
        enabled = action.enabled,
        colors = menuItemColors(),
        text = {
            Text(
                text = action.stringDesc.localized().personalizeGender(hebrewGender),
                style = MaterialTheme.typography.bodyLarge,
            )
        },
        trailingIcon = {
            Icon(
                imageVector = AppIcons.ChevronLeft,
                contentDescription = null,
            )
        },
        onClick = { onOpenSubMenu(action.id) },
    )
}

@Composable
private fun OverflowCheckboxRow(
    action: ToolbarActionConfig.CheckboxActionRes<*>,
    hebrewGender: HebrewGender,
) {
    DropdownMenuItem(
        enabled = action.enabled,
        colors = menuItemColors(),
        text = {
            Text(
                text = action.stringDesc.localized().personalizeGender(hebrewGender),
                style = MaterialTheme.typography.bodyLarge,
            )
        },
        trailingIcon = {
            Checkbox(
                checked = action.checked,
                onCheckedChange = null, // whole row clickable
                colors = CheckboxDefaults.colors(),
            )
        },
        onClick = { action.onCheckedChange(!action.checked) },
    )
}

@Composable
private fun <TId : Enum<TId>> OverflowSimpleActionRow(
    action: ToolbarActionConfig<TId>,
    hebrewGender: HebrewGender,
    onDismissAll: () -> Unit,
) {
    DropdownMenuItem(
        enabled = action.enabled,
        colors = menuItemColors(),
        text = {
            Text(
                text = actionOverflowText(action, hebrewGender),
                style = MaterialTheme.typography.bodyLarge,
            )
        },
        onClick = {
            onDismissAll()
            action.invokeClick()
        }
    )
}

@Composable
private fun SubMenuHeader(
    title: String,
    onBack: () -> Unit,
) {
    DropdownMenuItem(
        colors = menuItemColors(),
        text = {
            Text(
                text = title,
                style = MaterialTheme.typography.bodyLarge,
            )
        },
        leadingIcon = {
            Icon(
                imageVector = Icons.AutoMirrored.Filled.ArrowBack,
                contentDescription = null,
            )
        },
        onClick = onBack,
        enabled = true,
    )
}

@Composable
private fun <TId : Enum<TId>, TOption : Enum<TOption>> RadioSubMenuContent(
    content: SubMenuContent.RadioSubMenu<TId, TOption>,
    hebrewGender: HebrewGender,
    onDismissAll: () -> Unit,
) {
    content.options.forEach { option ->
        val selected = content.selected == option.option

        DropdownMenuItem(
            colors = menuItemColors(),
            text = {
                Text(
                    text = option.title.localized().personalizeGender(hebrewGender),
                    style = MaterialTheme.typography.bodyLarge,
                )
            },
            leadingIcon = {
                // This matches the “radio” visual from your screenshots.
                RadioButton(
                    selected = selected,
                    onClick = null,
                )
            },
            onClick = {
                content.onOptionSelected(option.option)
                onDismissAll()
            }
        )
    }
}

private fun <TId : Enum<TId>> ToolbarActionConfig<TId>.invokeClick() {
    when (this) {
        is ToolbarActionConfig.TextActionRes -> onClick()
        is ToolbarActionConfig.TextAction -> onClick()
        is ToolbarActionConfig.IconAction -> onClick()
        is ToolbarActionConfig.SubMenu -> {
            error("SubMenu does not support invokeClick() – it should be expanded, not clicked")
        }

        is ToolbarActionConfig.CheckboxActionRes -> {
            error("CheckboxAction does not support invokeClick() – use onCheckedChange")
        }
    }
}

@Composable
private fun <TId : Enum<TId>> actionOverflowText(
    action: ToolbarActionConfig<TId>,
    hebrewGender: HebrewGender,
): String {
    return when (action) {
        is ToolbarActionConfig.TextActionRes -> action.stringDesc.localized()
        is ToolbarActionConfig.TextAction -> action.text
        is ToolbarActionConfig.IconAction -> {
            action.menuText?.localized() ?: (action.contentDescription.orEmpty())
        }

        is ToolbarActionConfig.SubMenu -> action.stringDesc.localized()
        is ToolbarActionConfig.CheckboxActionRes -> action.stringDesc.localized()
    }.personalizeGender(hebrewGender)
}
